import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getVideoById, getRelatedVideos, type VideoItem } from '@/mocks/videos';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const video = id ? getVideoById(id) : undefined;
  const article = id ? getArticleById(id) : undefined;
  const item = video || article;

  const relatedVideos = id ? getRelatedVideos(id, 4) : [];
  const relatedArticles = id ? getRelatedArticles(id, 3) : [];

  if (!item) {
    return (
      <>
        <ScrollToTop />
        <main>
          <Navbar />
          <section className="pt-20 pb-8 min-h-screen flex items-center justify-center bg-background-50">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-950 mb-2 font-heading">内容未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的内容不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </section>
          <Footer />
        </main>
      </>
    );
  }

  const isArticle = 'content' in item;
  const categorySlug = item.category;
  const categoryInfo = categories.find((c) => c.slug === categorySlug);
  const pageTitle = `${item.title} - 茄子视频`;
  const pageDescription = 'excerpt' in item ? item.excerpt : item.description;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`茄子视频,${item.tags.join(',')},${item.categoryName},在线观看`}
        canonicalUrl={`https://qiezi.video/article/${item.id}`}
        ogType="article"
        pageType="article"
      />

      <main>
        <Navbar />

        {/* Breadcrumb */}
        <section className="pt-20 pb-4 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{categoryInfo?.name || item.categoryName}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium truncate max-w-[200px]">{item.title}</span>
            </nav>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <article className="flex-1 min-w-0">
                {/* Video Cover */}
                {'cover' in item && (
                  <div className="relative aspect-[16/9] rounded-lg overflow-hidden mb-6">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 cursor-pointer hover:bg-primary-500 transition-colors duration-200">
                        <i className="ri-play-fill text-background-50 text-2xl md:text-3xl"></i>
                      </div>
                    </div>
                    {'duration' in item && (
                      <span className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 text-background-50 text-sm rounded whitespace-nowrap">
                        {item.duration}
                      </span>
                    )}
                  </div>
                )}

                {/* Tags & Meta */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {item.tags.map((tag: string) => (
                    <span key={tag} className="text-xs px-2.5 py-1 bg-accent-100 text-accent-800 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                  {item.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {'author' in item ? item.author : item.categoryName}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {item.publishDate}
                  </span>
                  {'views' in item && (
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {(item.views / 10000).toFixed(1)}万次观看
                    </span>
                  )}
                  {'readTime' in item && (
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      阅读约{item.readTime}分钟
                    </span>
                  )}
                </div>

                {/* Article Content */}
                {isArticle && 'content' in item && (
                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                )}

                {/* Video Description */}
                {!isArticle && 'description' in item && (
                  <div className="bg-background-100 rounded-lg p-5 md:p-6">
                    <h2 className="text-lg font-semibold text-foreground-950 mb-3 font-heading">视频简介</h2>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{item.description}</p>
                    <div className="mt-4 p-4 bg-accent-50 rounded-lg border border-accent-200">
                      <p className="text-sm text-foreground-600 flex items-start gap-2">
                        <i className="ri-information-line text-accent-600 mt-0.5"></i>
                        此视频来自{item.categoryName}频道，点击播放按钮即可在线观看高清完整版。
                      </p>
                    </div>
                  </div>
                )}
              </article>

              {/* Sidebar */}
              <aside className="lg:w-[320px] flex-shrink-0">
                {/* Related Videos */}
                {relatedVideos.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-base font-semibold text-foreground-950 mb-4 font-heading flex items-center gap-2">
                      <i className="ri-play-list-2-line text-primary-500"></i>
                      相关视频
                    </h3>
                    <div className="flex flex-col gap-3">
                      {relatedVideos.map((v) => (
                        <Link
                          key={v.id}
                          to={`/article/${v.id}`}
                          className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                        >
                          <div className="relative w-28 h-[72px] flex-shrink-0 rounded overflow-hidden">
                            <img src={v.cover} alt={v.title} className="w-full h-full object-cover object-top" />
                            <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-background-50 text-[10px] rounded whitespace-nowrap">
                              {v.duration}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                              {v.title}
                            </h4>
                            <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1">
                              <i className="ri-eye-line"></i> {(v.views / 10000).toFixed(1)}万
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-base font-semibold text-foreground-950 mb-4 font-heading flex items-center gap-2">
                      <i className="ri-article-line text-accent-500"></i>
                      相关文章
                    </h3>
                    <div className="flex flex-col gap-3">
                      {relatedArticles.map((a) => (
                        <Link
                          key={a.id}
                          to={`/article/${a.id}`}
                          className="p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                        >
                          <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                            {a.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                            <span>{a.author}</span>
                            <span>·</span>
                            <span>{a.publishDate}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category Info */}
                {categoryInfo && (
                  <div className="p-4 rounded-lg bg-background-100">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 font-heading">频道信息</h3>
                    <div className={`w-10 h-10 mb-3 flex items-center justify-center rounded-full bg-gradient-to-br ${categoryInfo.color}`}>
                      <i className={`${categoryInfo.icon} text-background-50 text-base`}></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-950 mb-1">{categoryInfo.name}</p>
                    <p className="text-xs text-foreground-600 mb-3">{categoryInfo.description}</p>
                    <Link
                      to={`/category/${categoryInfo.slug}`}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-1"
                    >
                      浏览更多 <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}